import type { ReactNode } from 'react';
import { ChevronRight, Info } from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * The tiles the overview is built from.
 *
 * A tile that can be opened says so with an affordance rather than only a
 * cursor: on a phone there is no hover to discover it with, and "which of these
 * numbers can I check" is the first thing anyone asks of a dashboard.
 */

export type TileTone = 'primary' | 'good' | 'cash' | 'warn' | 'bad' | 'plain';

const TONE_BAR: Record<TileTone, string> = {
  primary: 'bg-primary',
  good: 'bg-success',
  cash: 'bg-chart-3',
  warn: 'bg-warning',
  bad: 'bg-destructive',
  plain: 'bg-border',
};

export function StatGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        {title}
      </h3>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">{children}</div>
    </section>
  );
}

export function StatTile({
  label,
  value,
  sub,
  tone = 'plain',
  tooltip,
  loading,
  busy,
  onClick,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  tone?: TileTone;
  tooltip?: string;
  /** Nothing to show yet: the figure is replaced by a skeleton. */
  loading?: boolean;
  /** A figure is on screen and being refreshed: it stays, dimmed, under a spinner. */
  busy?: boolean;
  onClick?: () => void;
}) {
  // The overlay is a sibling of the dimmed content, so the spinner keeps its
  // own contrast instead of fading with the figure underneath it.
  const refreshing = busy && !loading;
  const body = (
    <>
      <BusyOverlay show={refreshing} size="sm" />
      <div className={cn(refreshing && 'opacity-60 transition-opacity')}>
        <span
          aria-hidden
          className={cn(
            'absolute inset-y-0 left-0 w-[3px] rounded-l-xl',
            TONE_BAR[tone]
          )}
        />
        <div className="flex items-start gap-1.5">
          <p className="text-muted-foreground min-w-0 text-xs font-medium">
            {label}
          </p>
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="text-muted-foreground/70 mt-0.5 size-3 shrink-0" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
            </Tooltip>
          )}
          {onClick && (
            <ChevronRight className="text-muted-foreground/50 ml-auto size-3.5 shrink-0" />
          )}
      </div>

      {loading ? (
        <Skeleton className="mt-2 h-7 w-24" />
      ) : (
        <p className="mt-1.5 text-xl font-semibold tabular-nums">{value}</p>
      )}

      {sub && !loading && (
        <p className="text-muted-foreground mt-0.5 text-xs">{sub}</p>
      )}
      </div>
    </>
  );

  const shell =
    'bg-card relative overflow-hidden rounded-xl border p-3 pl-4 text-left';

  if (!onClick) return <div className={shell}>{body}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        shell,
        'hover:border-primary/50 hover:bg-accent/50 focus-visible:ring-ring/50 transition-colors outline-none focus-visible:ring-2'
      )}
    >
      {body}
    </button>
  );
}

/**
 * A share bar with its figure on the caption line.
 *
 * antd's Progress reserved a fixed 2em box for its own percentage, which fits
 * "86%" and overflows on "86.27%"; keeping the number out of the track means it
 * has as much room as it needs.
 */
export function Meter({
  label,
  value,
  percent,
  color,
}: {
  label?: string;
  value: string;
  percent: number;
  color?: string;
}) {
  return (
    <div className="min-w-24 space-y-1">
      <div className="text-muted-foreground flex items-baseline justify-between gap-2 text-xs">
        {label && <span>{label}</span>}
        <span className="ml-auto tabular-nums">{value}</span>
      </div>
      <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
        <div
          className="bg-primary h-full rounded-full"
          style={{
            width: `${Math.max(0, Math.min(100, percent))}%`,
            background: color,
          }}
        />
      </div>
    </div>
  );
}

/** A heading above a table, with optional one-line help underneath. */
export function SectionTitle({
  title,
  help,
  actions,
}: {
  title: string;
  help?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold">{title}</h3>
        {help && <p className="text-muted-foreground text-xs">{help}</p>}
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
}
